import Tr from '../../localization/LocalizationKeys';
import React, { useCallback, useState } from 'react';
import { NodeType } from '../../utils/types';

import _ from 'lodash';
import MinimalTreePage from '../NodePage/MinimalTreePage';
import RadioGroup from '../RadioGroup/RadioGroup';
import { Button, Stack, StackItem } from '@patternfly/react-core';
import { useTranslation } from '../../localization/useTranslation';

import './TreeSelection.css';

export const AllowedSelection = Object.freeze({
  BOTH: 'both',
  NODES: 'nodes',
  ASSETS: 'assets'
});

const findTreeRoot = selection => {
  if (selection.parent == null) {
    return selection;
  }
  return findTreeRoot(selection.parent);
};

// Select only important properties to store
const createSelection = selection => {
  // Already created a selection for this. Just return it
  if (selection.treeRoot) {
    return selection;
  }
  const treeRoot = findTreeRoot(selection);
  const newSelection = _.pick(selection, ['id', '_id', '_key', 'displayName', 'type', 'assetDescendants', 'modelId']);
  newSelection.treeRoot = _.pick(treeRoot, ['id', '_id', '_key', 'name', 'displayName', 'type']);
  newSelection.assetDescendants = _.map(newSelection.assetDescendants, createSelection);
  return newSelection;
};

const ALLOWED_SECTION_RADIOS = [
  { id: AllowedSelection.BOTH, label: Tr.component.treeSelection.both, value: [NodeType.Node, NodeType.Asset] },
  { id: AllowedSelection.NODES, label: Tr.component.treeSelection.nodes, value: [NodeType.Node] },
  { id: AllowedSelection.ASSETS, label: Tr.component.treeSelection.assets, value: [NodeType.Asset] }
];

const TreeSelection = ({ initialRoot, selections, onSelectionsChanged, onTreeRootChange, allowNodes = true, allowAssets = true, allowTypeChange = true }) => {
  const T = useTranslation();
  var initialMode;
  if (allowAssets && allowNodes) {
    initialMode = AllowedSelection.BOTH;
  } else if (allowAssets) {
    initialMode = AllowedSelection.ASSETS;
  } else {
    initialMode = AllowedSelection.NODES;
  }
  const [allowedSelection, setAllowedSelection] = useState(initialMode);

  const allowedSelections = _.find(ALLOWED_SECTION_RADIOS, { id: allowedSelection })?.value;

  const _onItemSelection = useCallback((treePageSelection) => {
    onSelectionsChanged(_.map(treePageSelection, createSelection));
  }, [onSelectionsChanged]);

  const onAllowedSelectionChange = useCallback((allowedSelection) => {
    // Remove selections that are not allowed in the new allowedSelection mode
    const types = ALLOWED_SECTION_RADIOS.find(radio => radio.id === allowedSelection).value;
    onSelectionsChanged(_.filter(selections, selection => _.includes(types, selection.type)));
    setAllowedSelection(allowedSelection);
  }, [onSelectionsChanged, selections]);

  const onClearSelection = useCallback(() => {
    onSelectionsChanged([]);
  }, [onSelectionsChanged]);

  return (
    <Stack hasGutter className={'tree-main'}>
      <StackItem>
        { allowTypeChange && <RadioGroup radios={ALLOWED_SECTION_RADIOS} value={allowedSelection} onValueChanged={onAllowedSelectionChange} /> }
      </StackItem>
      <StackItem>
        <Button onClick={onClearSelection}>{ T.common.clearselection }</Button>
      </StackItem>
      <StackItem isFilled className={'tree-container'}>
        <MinimalTreePage
          initialRoot={initialRoot}
          onTreeRootChange={onTreeRootChange}
          onItemSelected={_onItemSelection}
          allowedSelection={allowedSelections}
          initialSelectedNodes={selections}
        />
      </StackItem>
    </Stack>
  );
};

export default React.memo(TreeSelection);
